import { CustomElement } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { FLEX_CONTAINER_COLUMN, H_100, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { DataModel } from "@cervval.js/common/src/forms/dataModel";
import { PropertyConfig } from "@cervval.js/common/src/forms/propertyConfig";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { keyof } from "@cervval.js/common/src/tools/object.tools";
import { SECONDS_IN_ONE_HOUR } from "@cervval.js/common/src/tools/time.tools";
import { UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { diff, toDate } from "@cervval.js/common/src/tools/timestamps.tools.tsdate";
import { CjsGeneratedForm } from "../../../generatedForm/generatedForm";
import { GeneratedFormEditMode, GeneratedFormSaveMode } from "../../../generatedForm/generatedForm.types";
import { } from "../dayAgenda/dayAgenda.types";
import { DayAgendaModel } from "../dayAgenda/dayAgendaModel";

export interface DayAgendaCreateItemFormOptions<ItemObject, ColumnObject> {
    /** Column of item */
    column: ColumnObject;

    /** Item edited / viewed */
    item: ItemObject;

    /** Model of agenda, used to create/update item */
    agendaModel: DayAgendaModel<ItemObject, ColumnObject>;

    isUpdate?: boolean;

}

/** 
 * TODO Improve to handle item on multiple days (currently: unique day model)
 */
interface EditItemFormData {
    /** Start time of item */
    startTime: number;
    /** Finish time of time */
    finishTime: number;
    /** Title of item */
    title: string;
    /** Date of item */
    date: UnixTimestamp;
}

@CustomElement({
    name: "cjs-day-agenda-edit-item-form",
    style: require("./dayAgendaEditItemForm.scss"),
    elementCssClasses: [W_100, FLEX_CONTAINER_COLUMN, H_100]
})
export class CjsDayAgendaEditItemForm<ItemObject, ColumnObject> extends WebComponent {

    /** 
     * Attributes options of AgendaColumnView 
     * ! options are not bound to HTML attributes for better performance
     */
    @TypedWebComponentOptions()
    public readonly options!: WebComponentOptions<DayAgendaCreateItemFormOptions<ItemObject, ColumnObject>>;

    /** Form to edit item */
    public form = new CjsGeneratedForm<EditItemFormData>({
        formMode: {
            editMode: GeneratedFormEditMode.UPDATE,
            saveMode: GeneratedFormSaveMode.STATIC
        },
        layout: {
            columnsCount: 2
        }
    });

    /** Datamodel of edit form */
    public datamodel = new DataModel<EditItemFormData>();

    /** PropertyConfigs or form */
    public pcs: PropertyConfig[] = this._buildPropertyConfigs();

    constructor(options: DayAgendaCreateItemFormOptions<ItemObject, ColumnObject>) {
        super();
        this.options.setWithoutRefresh(options);
    }

    /** @inheritdoc */
    public override init(): void {
        this.append(this.form);
    }

    /** @inheritdoc */
    public override refresh(): void {
        this.refreshForm();
    }

    /** Refresh form */
    public refreshForm(): void {
        this.form.initialize(this.pcs, this.datamodel, this._computeFormData());
    }

    /** ComputeFormData */
    protected _computeFormData(): EditItemFormData[] {
        const item = this.options.item;
        const agendaModel = this.options.agendaModel;

        const itemStartTs = agendaModel.getItemStartTs(item);
        const itemFinishTs = agendaModel.getItemFinishTs(item);

        const isUpdate = Boolean(this.options.isUpdate);

        const itemDate = toDate(itemStartTs).beginOf("day");

        const startTime = diff(itemDate.uts, itemStartTs, "second");

        // TODO improve, Temporary here : startTime + 30min, handle magnetism or default finishDate here
        const finishTime = diff(itemDate.uts, itemFinishTs, "second") + (!isUpdate ? SECONDS_IN_ONE_HOUR * 0.5 : 0);

        return [
            {
                startTime,
                finishTime,
                date: itemDate.uts,
                title: agendaModel.getItemText(item)
            }
        ];
    }

    /** Parse date in form into  */
    public getItemToCreate(): Promise<ItemObject | null> {
        if (!this.form.validateForm()) { return Promise.resolve(null); }

        const formSave = this.form.getDataToCreate()[0];
        const agendaModel = this.options.agendaModel;

        // Convert seconds in form to uts
        const itemDate = toDate(formSave.date);
        const itemStartTs = itemDate.setValues({ secondOfDay: formSave.startTime }).uts;
        const itemFinishTs = itemDate.setValues({ secondOfDay: formSave.finishTime }).uts;

        if (this.options.isUpdate) {
            return agendaModel.updateItem(this.options.item, this.options.column, itemStartTs, itemFinishTs, formSave.title);
        } else {
            return agendaModel.createItem(this.options.column, itemStartTs, itemFinishTs, formSave.title);
        }
    }

    /** Get pcs of form */
    protected _buildPropertyConfigs(): PropertyConfig[] {
        // TODO Add options in agendaModel
        const pcs: PropertyConfig[] = [];
        pcs.push(
            {
                label: "Titre",// TODO i18n
                propertyName: keyof<EditItemFormData>("title"),
                typeUid: PropertyConfigType.STRING,
                options: {
                    layout: {
                        colSpan: 2,
                        appendLineBreak: true
                    },
                    placeholder: "Ajouter un titre"// TODO i18n
                }
            },
            {
                label: "Date",// TODO i18n
                propertyName: keyof<EditItemFormData>("date"),
                typeUid: PropertyConfigType.DATE,
                options: {
                    layout: {
                        colSpan: 2,
                        appendLineBreak: true
                    }
                }
            },
            {
                label: "Début",// TODO i18n
                propertyName: keyof<EditItemFormData>("startTime"),
                typeUid: PropertyConfigType.TIME_IN_SECONDS,
                options: {
                    layout: {
                        colSpan: 1,
                        appendLineBreak: true
                    }
                }
            },
            {
                label: "Fin",// TODO i18n
                propertyName: keyof<EditItemFormData>("finishTime"),
                typeUid: PropertyConfigType.TIME_IN_SECONDS,
                options: {
                    layout: {
                        colSpan: 1,
                        appendLineBreak: true
                    }
                }
            }
        );
        return pcs;
    }
}